import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CloudArrowUpIcon, TrashIcon, ArrowLeftIcon, ArrowRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch } from '../../hooks/redux';
import { updateProfile } from '../../store/slices/userSlice';
import { fetchCurrentUser } from '../../store/slices/authSlice';
import { uploadAPI } from '../../services/api';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import Switch from '../common/Switch';
import { compressImage } from '../../utils/compression';
import { resolveImageUrl } from '../../utils/image';
import { User } from '../../types';

interface EditModelProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

const GENRES = [
  'Editorial',
  'Fashion/Runway',
  'Commercial',
  'Swimwear',
  'Lingerie',
  'Fitness',
  'Parts (Hand/Foot)',
  'Glamour',
  'Artistic Nude',
  'Cosplay',
  'Fit Model'
];

const EditModelProfileModal: React.FC<EditModelProfileModalProps> = ({ isOpen, onClose, user }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');

  // Specs state
  const [heightCm, setHeightCm] = useState<number | ''>('');
  const [weightKg, setWeightKg] = useState<number | ''>('');
  const [bust, setBust] = useState<number | ''>('');
  const [waist, setWaist] = useState<number | ''>('');
  const [hips, setHips] = useState<number | ''>('');
  const [shoeSize, setShoeSize] = useState('');
  const [dressSize, setDressSize] = useState('');
  const [eyeColor, setEyeColor] = useState('');
  const [hairColor, setHairColor] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [gender, setGender] = useState('');

  // Rates state
  const [halfDayRate, setHalfDayRate] = useState<number | ''>('');
  const [fullDayRate, setFullDayRate] = useState<number | ''>('');
  const [usageRightsRate, setUsageRightsRate] = useState<number | ''>('');

  // Preferences & Agency
  const [agencyRepresented, setAgencyRepresented] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [acceptsTfp, setAcceptsTfp] = useState(true);
  const [acceptsPaid, setAcceptsPaid] = useState(true);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [digitals, setDigitals] = useState<string[]>([]);
  const [uploadingDigitals, setUploadingDigitals] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});

  // Imperial inputs (temp state)
  const [heightFt, setHeightFt] = useState<number | ''>('');
  const [heightIn, setHeightIn] = useState<number | ''>('');
  const [weightLbs, setWeightLbs] = useState<number | ''>('');
  const [bustIn, setBustIn] = useState<number | ''>('');
  const [waistIn, setWaistIn] = useState<number | ''>('');
  const [hipsIn, setHipsIn] = useState<number | ''>('');

  useEffect(() => {
    if (user?.model_profile) {
      const mp = user.model_profile;
      setHeightCm(mp.height || '');
      setWeightKg(mp.weight || '');
      setBust(mp.bust || '');
      setWaist(mp.waist || '');
      setHips(mp.hips || '');
      setShoeSize(mp.shoe_size || '');
      setDressSize(mp.dress_size || '');
      setEyeColor(mp.eye_color || '');
      setHairColor(mp.hair_color || '');
      setEthnicity(mp.ethnicity || '');
      setGender(mp.gender || '');
      setHalfDayRate(mp.half_day_rate || '');
      setFullDayRate(mp.full_day_rate || '');
      setUsageRightsRate(mp.usage_rights_rate || '');
      setAgencyRepresented(mp.agency_represented || false);
      setAgencyName(mp.agency_name || '');
      setAcceptsTfp(mp.accepts_tfp !== false); // default true
      setAcceptsPaid(mp.accepts_paid !== false); // default true
      setSelectedGenres(mp.shoot_preferences || []);
      setDigitals(mp.digitals || []);

      // Pre-fill imperial from metric
      if (mp.height) {
        const totalInches = mp.height / 2.54;
        setHeightFt(Math.floor(totalInches / 12));
        setHeightIn(Math.round(totalInches % 12));
      }
      if (mp.weight) setWeightLbs(Math.round(mp.weight * 2.20462));
      if (mp.bust) setBustIn(Math.round(mp.bust / 2.54));
      if (mp.waist) setWaistIn(Math.round(mp.waist / 2.54));
      if (mp.hips) setHipsIn(Math.round(mp.hips / 2.54));
    }
  }, [user]);

  // Synchronize metric & imperial values on switch
  const handleUnitSystemChange = (system: 'metric' | 'imperial') => {
    setUnitSystem(system);
    if (system === 'metric') {
      // Imperial to Metric
      if (heightFt !== '' || heightIn !== '') {
        const ft = Number(heightFt || 0);
        const inches = Number(heightIn || 0);
        setHeightCm(Math.round((ft * 12 + inches) * 2.54));
      }
      if (weightLbs !== '') setWeightKg(Math.round(Number(weightLbs) / 2.20462));
      if (bustIn !== '') setBust(Math.round(Number(bustIn) * 2.54));
      if (waistIn !== '') setWaist(Math.round(Number(waistIn) * 2.54));
      if (hipsIn !== '') setHips(Math.round(Number(hipsIn) * 2.54));
    } else {
      // Metric to Imperial
      if (heightCm !== '') {
        const totalInches = Number(heightCm) / 2.54;
        setHeightFt(Math.floor(totalInches / 12));
        setHeightIn(Math.round(totalInches % 12));
      }
      if (weightKg !== '') setWeightLbs(Math.round(Number(weightKg) * 2.20462));
      if (bust !== '') setBustIn(Math.round(Number(bust) / 2.54));
      if (waist !== '') setWaistIn(Math.round(Number(waist) / 2.54));
      if (hips !== '') setHipsIn(Math.round(Number(hips) / 2.54));
    }
  };

  // Genre select/toggle
  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
  };

  // Digital photos uploader
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    onDrop: async (acceptedFiles) => {
      setUploadingDigitals(true);
      const total = acceptedFiles.length;
      const uploadedUrls: string[] = [];

      for (let i = 0; i < total; i++) {
        const file = acceptedFiles[i];
        const localUrl = URL.createObjectURL(file);
        
        try {
          setUploadStatus(`Compressing image ${i + 1} of ${total}...`);
          const compressed = await compressImage(file);
          
          setUploadStatus(`Uploading image ${i + 1} of ${total} (0%)...`);
          const formData = new FormData();
          formData.append('image', compressed);
          
          const res = await uploadAPI.uploadImage(formData, (progressEvent: any) => {
            const totalBytes = progressEvent.total || compressed.size;
            const percent = Math.round((progressEvent.loaded * 100) / totalBytes);
            setUploadStatus(`Uploading image ${i + 1} of ${total} (${percent}%)...`);
          });
          
          if (res.data.success) {
            const uploadedPath = res.data.data.url;
            setLocalPreviews(prev => ({
              ...prev,
              [uploadedPath]: localUrl
            }));
            uploadedUrls.push(uploadedPath);
          }
        } catch (e) {
          console.error('Failed to upload digital photo:', e);
        }
      }

      if (uploadedUrls.length > 0) {
        setDigitals((prev) => [...prev, ...uploadedUrls]);
        toast.success(`Successfully uploaded ${uploadedUrls.length} digital(s)`);
      } else {
        toast.error('Failed to upload images');
      }
      setUploadingDigitals(false);
      setUploadStatus('');
    }
  });

  const removeDigital = (indexToRemove: number) => {
    setDigitals((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const moveDigital = (index: number, direction: 'left' | 'right') => {
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= digitals.length) return;
    
    const updated = [...digitals];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    
    setDigitals(updated);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Final unit sync to Metric before save
    let finalHeight = heightCm;
    let finalWeight = weightKg;
    let finalBust = bust;
    let finalWaist = waist;
    let finalHips = hips;

    if (unitSystem === 'imperial') {
      if (heightFt !== '' || heightIn !== '') {
        finalHeight = Math.round((Number(heightFt || 0) * 12 + Number(heightIn || 0)) * 2.54);
      }
      if (weightLbs !== '') finalWeight = Math.round(Number(weightLbs) / 2.20462);
      if (bustIn !== '') finalBust = Math.round(Number(bustIn) * 2.54);
      if (waistIn !== '') finalWaist = Math.round(Number(waistIn) * 2.54);
      if (hipsIn !== '') finalHips = Math.round(Number(hipsIn) * 2.54);
    }

    const payload = {
      model_profile: {
        height: finalHeight ? Number(finalHeight) : undefined,
        weight: finalWeight ? Number(finalWeight) : undefined,
        bust: finalBust ? Number(finalBust) : undefined,
        waist: finalWaist ? Number(finalWaist) : undefined,
        hips: finalHips ? Number(finalHips) : undefined,
        shoe_size: shoeSize || undefined,
        dress_size: dressSize || undefined,
        eye_color: eyeColor || undefined,
        hair_color: hairColor || undefined,
        ethnicity: ethnicity || undefined,
        gender: gender || undefined,
        half_day_rate: halfDayRate ? Number(halfDayRate) : undefined,
        full_day_rate: fullDayRate ? Number(fullDayRate) : undefined,
        usage_rights_rate: usageRightsRate ? Number(usageRightsRate) : undefined,
        agency_represented: agencyRepresented,
        agency_name: agencyRepresented ? agencyName : '',
        accepts_tfp: acceptsTfp,
        accepts_paid: acceptsPaid,
        shoot_preferences: selectedGenres,
        digitals: digitals
      }
    };

    try {
      const result = await dispatch(updateProfile(payload));
      if (updateProfile.fulfilled.match(result)) {
        toast.success('Model specifications updated successfully!');
        dispatch(fetchCurrentUser(user.id)); // Refresh local authentication credentials
        onClose();
      } else {
        toast.error('Failed to update specifications');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while saving specifications');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white dark:bg-gray-900 border border-gray-150 dark:border-gray-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
            <div>
              <h2 className="text-xl font-extrabold text-gray-950 dark:text-white">Model Customization</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Configure your physical parameters, booking details, and digital portfolio cards.</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-8">
            
            {/* Units Toggle */}
            <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/40 p-4 rounded-2xl">
              <div>
                <span className="text-sm font-semibold text-gray-950 dark:text-white block">Measurement System</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Choose metric or imperial display parameters</span>
              </div>
              <div className="flex bg-gray-200 dark:bg-gray-800 p-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => handleUnitSystemChange('metric')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    unitSystem === 'metric'
                      ? 'bg-white dark:bg-gray-900 text-teal-600 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                  }`}
                >
                  Metric (cm/kg)
                </button>
                <button
                  type="button"
                  onClick={() => handleUnitSystemChange('imperial')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    unitSystem === 'imperial'
                      ? 'bg-white dark:bg-gray-900 text-teal-600 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                  }`}
                >
                  Imperial (ft/in/lbs)
                </button>
              </div>
            </div>

            {/* Physical Attributes Section */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Physical Specifications</h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {/* Height */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Height</label>
                  {unitSystem === 'metric' ? (
                    <div className="relative">
                      <input
                        type="number"
                        value={heightCm}
                        onChange={(e) => setHeightCm(e.target.value ? parseInt(e.target.value) : '')}
                        placeholder="175"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                      />
                      <span className="absolute right-3 top-2.5 text-xs text-gray-400">cm</span>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          value={heightFt}
                          onChange={(e) => setHeightFt(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="5"
                          className="w-full px-2 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-2 top-2.5 text-[10px] text-gray-400">ft</span>
                      </div>
                      <div className="relative flex-1">
                        <input
                          type="number"
                          value={heightIn}
                          onChange={(e) => setHeightIn(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="9"
                          className="w-full px-2 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-2 top-2.5 text-[10px] text-gray-400">in</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Weight */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Weight</label>
                  <div className="relative">
                    {unitSystem === 'metric' ? (
                      <>
                        <input
                          type="number"
                          value={weightKg}
                          onChange={(e) => setWeightKg(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="60"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">kg</span>
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          value={weightLbs}
                          onChange={(e) => setWeightLbs(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="132"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">lbs</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                  >
                    <option value="">Select Gender</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Non-Binary">Non-Binary</option>
                    <option value="Genderfluid">Genderfluid</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Bust */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Bust / Chest</label>
                  <div className="relative">
                    {unitSystem === 'metric' ? (
                      <>
                        <input
                          type="number"
                          value={bust}
                          onChange={(e) => setBust(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="90"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">cm</span>
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          value={bustIn}
                          onChange={(e) => setBustIn(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="35"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">in</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Waist */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Waist</label>
                  <div className="relative">
                    {unitSystem === 'metric' ? (
                      <>
                        <input
                          type="number"
                          value={waist}
                          onChange={(e) => setWaist(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="60"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">cm</span>
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          value={waistIn}
                          onChange={(e) => setWaistIn(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="24"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">in</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Hips */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Hips</label>
                  <div className="relative">
                    {unitSystem === 'metric' ? (
                      <>
                        <input
                          type="number"
                          value={hips}
                          onChange={(e) => setHips(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="90"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">cm</span>
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          value={hipsIn}
                          onChange={(e) => setHipsIn(e.target.value ? parseInt(e.target.value) : '')}
                          placeholder="35"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">in</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Shoe Size */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Shoe Size</label>
                  <input
                    type="text"
                    value={shoeSize}
                    onChange={(e) => setShoeSize(e.target.value)}
                    placeholder="EU 39 / US 8"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                  />
                </div>

                {/* Dress Size */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Dress Size</label>
                  <input
                    type="text"
                    value={dressSize}
                    onChange={(e) => setDressSize(e.target.value)}
                    placeholder="S / EU 36"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                  />
                </div>

                {/* Hair Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Hair Color</label>
                  <input
                    type="text"
                    value={hairColor}
                    onChange={(e) => setHairColor(e.target.value)}
                    placeholder="Brown / Blonde"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                  />
                </div>

                {/* Eye Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Eye Color</label>
                  <input
                    type="text"
                    value={eyeColor}
                    onChange={(e) => setEyeColor(e.target.value)}
                    placeholder="Hazel / Blue"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                  />
                </div>

                {/* Ethnicity */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ethnicity</label>
                  <input
                    type="text"
                    value={ethnicity}
                    onChange={(e) => setEthnicity(e.target.value)}
                    placeholder="E.g., Mixed"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                  />
                </div>
              </div>
            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Rates Section */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Pricing details</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Half Day */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Half Day Rate (4 hrs)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs text-gray-400">₹</span>
                    <input
                      type="number"
                      value={halfDayRate}
                      onChange={(e) => setHalfDayRate(e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="5,000"
                      className="w-full pl-7 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                    />
                  </div>
                </div>

                {/* Full Day */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Full Day Rate (8 hrs)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs text-gray-400">₹</span>
                    <input
                      type="number"
                      value={fullDayRate}
                      onChange={(e) => setFullDayRate(e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="10,000"
                      className="w-full pl-7 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                    />
                  </div>
                </div>

                {/* Licensing Rate */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Usage / Commercial Rights</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs text-gray-400">₹</span>
                    <input
                      type="number"
                      value={usageRightsRate}
                      onChange={(e) => setUsageRightsRate(e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="2,500"
                      className="w-full pl-7 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                    />
                  </div>
                </div>
              </div>
            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Safety & Preferences Section */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Safety & Preferences</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-gray-50 dark:bg-gray-800/20 p-5 rounded-2xl">
                {/* Agency Representation */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-950 dark:text-white">Agency Represented</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Toggle if exclusive or non-exclusive representation applies</p>
                    </div>
                    <Switch checked={agencyRepresented} onChange={setAgencyRepresented} />
                  </div>
                  {agencyRepresented && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="pt-1"
                    >
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Agency Name</label>
                      <input
                        type="text"
                        value={agencyName}
                        onChange={(e) => setAgencyName(e.target.value)}
                        placeholder="E.g., Elite Models"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl text-sm"
                      />
                    </motion.div>
                  )}
                </div>

                {/* Shoot Boundaries */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-950 dark:text-white">Accepts Paid Work</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Open to commercial and client-sponsored bookings</p>
                    </div>
                    <Switch checked={acceptsPaid} onChange={setAcceptsPaid} />
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-3">
                    <div>
                      <span className="text-sm font-semibold text-gray-950 dark:text-white">Accepts TFP (Print / Collabs)</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Open to non-paid test shoots to build portfolio</p>
                    </div>
                    <Switch checked={acceptsTfp} onChange={setAcceptsTfp} />
                  </div>
                </div>
              </div>

              {/* Shoot Genres */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Accepted Shoot Genres / Boundaries</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {GENRES.map((genre) => {
                    const isSelected = selectedGenres.includes(genre);
                    return (
                      <button
                        type="button"
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`px-3 py-2 text-xs text-left border rounded-xl font-medium transition-all ${
                          isSelected
                            ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-500 text-teal-700 dark:text-teal-400'
                            : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800'
                        }`}
                      >
                        {genre}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Digitals / Polaroids Section */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Digitals / Polaroids</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload natural, raw, unedited photos (agencies always ask for these).</p>
              </div>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? 'border-teal-500 bg-teal-50/30 dark:bg-teal-900/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <input {...getInputProps()} />
                {uploadingDigitals ? (
                  <div className="flex flex-col items-center justify-center space-y-2 py-2">
                    <ArrowPathIcon className="w-8 h-8 text-teal-600 animate-spin" />
                    <span className="text-xs font-bold text-teal-600 dark:text-teal-400 block">{uploadStatus}</span>
                  </div>
                ) : (
                  <>
                    <CloudArrowUpIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 block">
                      Drop natural digitals here, or click to upload
                    </span>
                    <span className="text-[10px] text-gray-400 block mt-1">Support JPEG, PNG, WEBP</span>
                  </>
                )}
              </div>

              {digitals.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {digitals.map((url, idx) => (
                    <div key={url} className="relative aspect-[3/4] rounded-2xl overflow-hidden group bg-gray-100 border dark:border-gray-800">
                      <img src={localPreviews[url] || resolveImageUrl(url)} alt={`Digital Polaroid ${idx + 1}`} className="w-full h-full object-cover" />
                      
                      {/* Featured Look Badge for the first 4 images */}
                      {idx < 4 && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-teal-600/90 backdrop-blur-sm text-white text-[9px] font-black tracking-wider uppercase rounded shadow-sm">
                          Look {idx + 1}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeDigital(idx)}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        title="Remove look"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>

                      {/* Reorder shift buttons */}
                      <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => moveDigital(idx, 'left')}
                            className="p-1 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors shadow-md"
                            title="Move Left"
                          >
                            <ArrowLeftIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {idx < digitals.length - 1 && (
                          <button
                            type="button"
                            onClick={() => moveDigital(idx, 'right')}
                            className="p-1 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors shadow-md"
                            title="Move Right"
                          >
                            <ArrowRightIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

          </form>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 flex justify-end gap-3 rounded-b-3xl">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={loading}>
              Save Specifications
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EditModelProfileModal;
